import React from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import PersonIcon from '@material-ui/icons/Person';
import Typography from '@material-ui/core/Typography';
import { blue } from '@material-ui/core/colors';
import Spin from 'components/common/Spin';
import cloneDeep from 'lodash/cloneDeep';
import {
  GET_CONVOS,
  ADD_USER_TO_CONVO,
  GET_STUDENTS_PROFILES,
} from 'graphql/conversations';
import { getFullName } from 'utils';
import { useMutation } from '@apollo/react-hooks';
import useSnack from 'hooks/useSnack';
import { USER_TYPES } from 'constant/userTypes';

const useStyles = makeStyles({
  avatar: {
    backgroundColor: blue[100],
    color: blue[600],
  },
});

function AddUsersModal(props) {
  const classes = useStyles();
  const { onClose, open, student, users } = props;

  const handleClose = () => {
    onClose();
  };

  const handleListItemClick = (value) => {
    onClose(value);
  };

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle id="simple-dialog-title">Select User</DialogTitle>
      <Query
        query={GET_STUDENTS_PROFILES}
        variables={{ userId: student.user_id }}
      >
        {({ data, loading }) => {
          const studentDetails = data?.rows[0];
          const rows = [];
          if (studentDetails) {
            if (!users.find(({ user }) => user.user_id === student.user_id)) {
              rows.push(student);
            }
            studentDetails.guardians.forEach(({ guardian }) => {
              if (
                !users.find(({ user }) => user.user_id === guardian.user_id)
              ) {
                rows.push(guardian);
              }
            });
            if (
              !users.find(
                ({ user }) => user?.user_id === studentDetails.hc_user?.user_id,
              )
            ) {
              rows.push(studentDetails.hc_user);
            }
            if (
              !users.find(
                ({ user }) => user?.user_id === studentDetails.lc_user?.user_id,
              )
            ) {
              rows.push(studentDetails.lc_user);
            }
          }

          return (
            <Spin spinning={loading}>
              <List>
                {rows.map((user) => user && (
                  <ListItem
                    button
                    onClick={() => handleListItemClick(user)}
                    key={user?.user_id}
                  >
                    <ListItemAvatar>
                      <Avatar src={user?.avatar} className={classes.avatar}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={getFullName(user)}
                      secondary={USER_TYPES[user?.user_type]}
                    />
                  </ListItem>
                ))}
              </List>
            </Spin>
          );
        }}
      </Query>
    </Dialog>
  );
}

AddUsersModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default function AddUser({ users, student, convoId, type }) {
  const snack = useSnack();
  const [open, setOpen] = React.useState(false);
  const [addUser] = useMutation(ADD_USER_TO_CONVO);

  const handleClickOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleClose = (value) => {
    setOpen(false);
    if (value) {
      onSelect(value);
    }
  };

  const onSelect = (user) => {
    if (user) {
      addUser({
        variables: {
          convoId,
          userId: user.user_id,
        },
      }).then(
        () => {
          snack.success('User added to conversation');
        },
        (e) => snack.error(e.message),
      );
    }
  };

  return (
    <div>
      <Typography
        color="primary"
        variant="caption"
        component="a"
        href="#"
        onClick={handleClickOpen}
      >
        Add users to conversation
      </Typography>
      <AddUsersModal
        open={open}
        onClose={handleClose}
        student={student}
        users={users}
      />
    </div>
  );
}
